/**
 * Step 2: Select Item & Location
 * Generic item and location selector for all inventory types
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, InputAdornment, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, useTheme, alpha, Checkbox, Chip, Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { supabase } from '../../../../../supabaseClient';

export default function StepSelectItem({
  adjustmentType,
  selectedItems,
  onSelectItems,
  selectedLocation,
  onSelectLocation,
  toLocation,
  onSelectToLocation,
  items,
  locations,
  config,
  companyId,
  selectedVariant,
  onSelectVariant
}) {
  const theme = useTheme();
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [toLocationDialogOpen, setToLocationDialogOpen] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [toLocationSearch, setToLocationSearch] = useState('');
  const [tempSelectedItems, setTempSelectedItems] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);

  const isProduct = config?.hasUnitConfig;

  // Extract numeric size from variant name for sorting (e.g., "16oz" -> 16)
  const extractSize = (name) => {
    const match = name?.match(/(\d+(\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  };

  // Support both single variant (legacy) and multi-variant selection
  const selectedVariants = Array.isArray(selectedVariant) ? selectedVariant : (selectedVariant ? [selectedVariant] : []);

  // Fetch variants when a single product is selected
  useEffect(() => {
    if (!isProduct || selectedItems.length !== 1 || !companyId) {
      setProductVariants([]);
      return;
    }
    const product = selectedItems[0];
    (async () => {
      setLoadingVariants(true);
      const { data } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', product.id)
        .eq('is_active', true);
      setProductVariants(data || []);
      setLoadingVariants(false);
      // Clear selected variants that no longer exist
      if (onSelectVariant && selectedVariants.length > 0) {
        const validVariants = selectedVariants.filter(sv => data?.some(v => v.id === sv.id));
        if (validVariants.length !== selectedVariants.length) {
          onSelectVariant(validVariants);
        }
      }
    })();
  }, [selectedItems, isProduct, companyId]);

  const filteredItems = items.filter(item =>
    item.name?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const filteredLocations = locations
    .filter(loc => loc.name?.toLowerCase().includes(locationSearch.toLowerCase()))
    .sort((a, b) => {
      // Main location first
      if (a.is_main && !b.is_main) return -1;
      if (!a.is_main && b.is_main) return 1;
      return 0;
    });

  const filteredToLocations = locations
    .filter(loc => 
      loc.name?.toLowerCase().includes(toLocationSearch.toLowerCase()) &&
      loc.id !== selectedLocation?.id
    )
    .sort((a, b) => {
      // Main location first
      if (a.is_main && !b.is_main) return -1;
      if (!a.is_main && b.is_main) return 1;
      return 0;
    });

  const itemLabel = config.displayName.slice(0, -1); // Remove trailing 's'

  const handleToggleItem = (item) => {
    setTempSelectedItems(prev => {
      const isSelected = prev.some(i => i.id === item.id);
      if (isSelected) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleOpenItemDialog = () => {
    setTempSelectedItems(selectedItems || []);
    setItemDialogOpen(true);
  };

  const handleApplyItemSelection = () => {
    onSelectItems(tempSelectedItems);
    setItemDialogOpen(false);
    setItemSearch('');
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Select the {itemLabel.toLowerCase()} and location for this transaction
      </Typography>

      <Grid container spacing={3}>
        {/* Item Selection */}
        <Grid item xs={12}>
          <Box>
            <Typography variant="body2" sx={{ 
              mb: 1, 
              fontWeight: 500, 
              color: 'text.secondary',
              fontSize: '0.9rem'
            }}>
              {itemLabel}s (Multiple Selection)
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleOpenItemDialog}
              sx={{
                justifyContent: 'space-between',
                textAlign: 'left',
                minHeight: 56,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                borderColor: alpha(theme.palette.primary.main, 0.3),
                borderWidth: 1,
                color: selectedItems?.length > 0 ? 'text.primary' : 'text.secondary',
                fontWeight: 500,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontSize: '0.9rem',
                display: 'block',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }}
            >
              {selectedItems?.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {selectedItems.map(item => (
                    <Chip
                      key={item.id}
                      label={item.name}
                      size="small"
                      sx={{ fontSize: '0.85rem' }}
                    />
                  ))}
                </Stack>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Select {itemLabel}s</span>
                  <SearchIcon />
                </Box>
              )}
            </Button>
          </Box>
        </Grid>

        {/* Product Variant Selection */}
        {isProduct && selectedItems.length === 1 && productVariants.length > 0 && (
          <Grid item xs={12}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                Select Variant:
              </Typography>
              <Table size="small" sx={{ bgcolor: 'white' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Variant</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...productVariants].sort((a, b) => extractSize(b.name) - extractSize(a.name)).map((variant) => {
                    const isSelected = selectedVariants.some(sv => sv.id === variant.id);
                    return (
                      <TableRow
                        key={variant.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2,
                            pl: 3,
                            pr: 3
                          }
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {variant.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              SKU: {variant.sku || 'No SKU'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              if (isSelected) {
                                onSelectVariant?.(selectedVariants.filter(sv => sv.id !== variant.id));
                              } else {
                                onSelectVariant?.([...selectedVariants, variant]);
                              }
                            }}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              px: 2,
                              py: 1,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              bgcolor: isSelected ? 'black' : 'transparent',
                              color: isSelected ? 'white' : 'inherit',
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': {
                                border: `1px solid ${theme.palette.primary.main}`,
                                bgcolor: isSelected ? 'black' : 'transparent',
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                              }
                            }}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Grid>
        )}

        {/* Product with no variants */}
        {isProduct && selectedItems.length === 1 && !loadingVariants && productVariants.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{
              p: 2, borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
              display: 'flex', alignItems: 'center', gap: 1
            }}>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                This product has no variants. Ready to proceed.
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Location Selection */}
        <Grid item xs={12}>
          <Box>
            <Typography variant="body2" sx={{ 
              mb: 1, 
              fontWeight: 500, 
              color: 'text.secondary',
              fontSize: '0.9rem'
            }}>
              {adjustmentType === 'transfer' ? 'From Location' : 'Location'}
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setLocationDialogOpen(true)}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                py: 2,
                px: 2,
                height: 56,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                color: selectedLocation ? 'text.primary' : 'text.secondary',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.9rem',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)'
                }
              }}
            >
              {selectedLocation ? selectedLocation.name : 'Select Location'}
            </Button>
          </Box>
        </Grid>

        {/* To Location (Transfer only) */}
        {adjustmentType === 'transfer' && (
          <Grid item xs={12}>
            <Box>
              <Typography variant="body2" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
                Transfer To Location
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setToLocationDialogOpen(true)}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  py: 2,
                  px: 2,
                  height: 56,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  color: toLocation ? 'text.primary' : 'text.secondary',
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)'
                  }
                }}
              >
                {toLocation ? toLocation.name : 'Select Destination Location'}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Item Selection Dialog */}
      <Dialog
        open={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '70vh',
            width: '90vw',
            maxWidth: '1400px'
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 3,
          pt: 3,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>Select {itemLabel}</Typography>
          <IconButton onClick={() => setItemDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            fullWidth
            placeholder={`Search ${itemLabel.toLowerCase()}s...`}
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ 
              mt: 3,
              mb: 4,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem'
              }
            }}
          />
          <Table sx={{ 
            '& .MuiTableCell-root': { 
              fontSize: '1rem',
              py: 2.5 
            } 
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500, width: 60 }}></TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => {
                const isSelected = tempSelectedItems.some(i => i.id === item.id);
                return (
                  <TableRow 
                    key={item.id} 
                    hover 
                    onClick={() => handleToggleItem(item)}
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      '&:hover': { 
                        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`
                      }
                    }}
                  >
                    <TableCell sx={{ width: 60 }} align="center">
                      <Checkbox
                        checked={isSelected}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleItem(item);
                        }}
                        sx={{
                          color: theme.palette.primary.main,
                          '&.Mui-checked': {
                            color: theme.palette.primary.main
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.95rem' }}>{item.name}</TableCell>
                  </TableRow>
                );
              })}
              {filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography color="text.secondary" sx={{ py: 4, fontSize: '1rem' }}>
                      No {itemLabel.toLowerCase()}s found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <Button 
            onClick={() => {
              setItemDialogOpen(false);
              setItemSearch('');
            }}
            sx={{ borderRadius: 2, px: 3, py: 1.2, fontSize: '0.95rem' }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApplyItemSelection}
            disabled={tempSelectedItems.length === 0}
            sx={{ borderRadius: 2, px: 3, py: 1.2, fontSize: '0.95rem' }}
          >
            Confirm Selection ({tempSelectedItems.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Selection Dialog */}
      <Dialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '60vh',
            width: '80vw',
            maxWidth: '1200px'
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 3,
          pt: 3,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {adjustmentType === 'transfer' ? 'Select From Location' : 'Select Location'}
          </Typography>
          <IconButton onClick={() => setLocationDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            fullWidth
            placeholder="Search locations..."
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ 
              mt: 3,
              mb: 4,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem'
              }
            }}
          />
          <Table sx={{ 
            '& .MuiTableCell-root': { 
              fontSize: '1rem',
              py: 2.5 
            } 
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Location</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLocations.map((loc) => (
                <TableRow key={loc.id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                  <TableCell sx={{ fontSize: '0.95rem' }}>{loc.name}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        onSelectLocation(loc);
                        setLocationDialogOpen(false);
                        setLocationSearch('');
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontSize: '0.95rem'
                      }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* To Location Dialog (Transfer only) */}
      <Dialog
        open={toLocationDialogOpen}
        onClose={() => setToLocationDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '60vh',
            width: '80vw',
            maxWidth: '1200px'
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 3,
          pt: 3,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>Select To Location</Typography>
          <IconButton onClick={() => setToLocationDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            fullWidth
            placeholder="Search locations..."
            value={toLocationSearch}
            onChange={(e) => setToLocationSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ 
              mt: 3,
              mb: 4,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem'
              }
            }}
          />
          <Table sx={{ 
            '& .MuiTableCell-root': { 
              fontSize: '1rem',
              py: 2.5 
            } 
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Location</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredToLocations.map((loc) => (
                <TableRow key={loc.id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                  <TableCell sx={{ fontSize: '0.95rem' }}>{loc.name}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        onSelectToLocation(loc);
                        setToLocationDialogOpen(false);
                        setToLocationSearch('');
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontSize: '0.95rem'
                      }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
